import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config';
import { useRolePermissions, filterUsersByRole } from '../hooks/useRolePermissions';

// CSS styly pro mobilní optimalizaci
const mobileStyles = `
  @media (max-width: 768px) {
    /* Skrytí URL baru v mobilních prohlížečích */
    .chat-mobile-fullscreen {
      height: 100vh;
      height: 100dvh; /* Dynamic viewport height pro moderní prohlížeče */
      /* Posun vršku chatu dolů pro iPhone s výřezem */
      padding-top: calc(env(safe-area-inset-top) + 20px) !important;
    }
    
    /* Optimalizace pro iOS Safari */
    @supports (-webkit-touch-callout: none) {
      .ios-chat-fix {
        /* Prevence překrytí URL barem */
        padding-bottom: calc(env(safe-area-inset-bottom) + 80px) !important;
      }
      
      .chat-mobile-fullscreen {
        /* Větší padding pro iPhone s výřezem */
        padding-top: calc(env(safe-area-inset-top) + 30px) !important;
      }
    }
    
    /* Android Chrome optimalizace */
    @media screen and (max-height: 700px) {
      .ios-chat-fix {
        padding-bottom: calc(env(safe-area-inset-bottom) + 100px) !important;
      }
    }
  }
`;

// Vložení stylů do DOM
if (typeof document !== 'undefined' && !document.getElementById('chat-mobile-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'chat-mobile-styles';
  styleElement.textContent = mobileStyles;
  document.head.appendChild(styleElement);
}

export default function Chat({ currentUserId, users, isAdmin, userRole }) {
  // Role permissions
  const { permissions, hasPermission } = useRolePermissions(userRole);
  
  // Filtrování uživatelů podle role permissions
  const visibleUsers = hasPermission('viewAllUsers') 
    ? users.filter(u => u._id !== currentUserId)
    : []; // Test role nevidí ostatní uživatele v private chatech

  const [isOpen, setIsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadByUser, setUnreadByUser] = useState({}); // Nepřečtené zprávy per uživatel
  const [isGroupChat, setIsGroupChat] = useState(false);
  const [isAIChat, setIsAIChat] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [showEmojis, setShowEmojis] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showChatView, setShowChatView] = useState(false); // Pro mobilní navigaci mezi seznamem a chatem
  const [aiTyping, setAiTyping] = useState(false); // Pro zobrazení "AI píše..."
  const [correctingAI, setCorrectingAI] = useState(null); // ID zprávy, kterou opravujeme
  const [correctionText, setCorrectionText] = useState(''); // Text opravy
  
  // Discord stavy
  const [isDiscordChat, setIsDiscordChat] = useState(false);
  const [discordAuth, setDiscordAuth] = useState(() => {
    // Načti uložené Discord credentials z localStorage
    const saved = localStorage.getItem('discordAuth');
    return saved ? JSON.parse(saved) : null;
  });
  const [discordChannels, setDiscordChannels] = useState([]);
  const [discordCategories, setDiscordCategories] = useState([]);
  const [discordChannelsByCategory, setDiscordChannelsByCategory] = useState({});
  const [discordUncategorized, setDiscordUncategorized] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [discordMessages, setDiscordMessages] = useState([]);
  const [selectedDiscordChannel, setSelectedDiscordChannel] = useState(null);
  const [discordWebhookUrl, setDiscordWebhookUrl] = useState('');
  const [showDiscordLogin, setShowDiscordLogin] = useState(false);
  const [selectedMediaFile, setSelectedMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // Loading stavy
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingDiscord, setIsLoadingDiscord] = useState(false);
  
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);
  const isOpenRef = useRef(isOpen);
  const selectedUserRef = useRef(null);

  // Emoji palette
  const emojis = ['😀', '😃', '😄', '😁', '😊', '😍', '🤔', '😎', '😢', '😭', '😡', '🤬', '👍', '👎', '👌', '✋', '🙏', '💪', '🎉', '🎊', '❤️', '💙', '💚', '💛', '🧡', '💜'];

  // Sledování velikosti okna pro mobilní verzi
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Sync refs se stavem aby polling interval používal aktuální hodnoty bez restartu
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { selectedUserRef.current = selectedUser; }, [selectedUser]);

  // Polling pro online status a nové zprávy
  useEffect(() => {
    if (!currentUserId || currentUserId === 'online') {
      return;
    }
    
    console.log('Setting up polling for user:', currentUserId);
    // Resetuj unread count při změně uživatele
    setUnreadCount(0);
    
    updateOnlineStatus();
    
    // První načtení bez notifikací - pouze inicializace
    const initializeUnreadMessages = async () => {
      try {
        console.log('🔄 Initial unread messages check (no notifications)');
        const response = await fetch(`${API_URL}/messages/unread/${currentUserId}`);
        if (response.ok) {
          const unreadMessages = await response.json();
          
          // Nastav unread count a per-user data
          setUnreadCount(unreadMessages.length);
          
          const unreadPerUser = {};
          unreadMessages.forEach(msg => {
            const senderId = typeof msg.senderId === 'object' ? msg.senderId._id : msg.senderId;
            
            // Pokud je zpráva skupinová, přiřaď ji ke klíči 'group'
            if (msg.isGroupMessage) {
              if (!unreadPerUser['group']) {
                unreadPerUser['group'] = [];
              }
              unreadPerUser['group'].push(msg);
            } else if (senderId !== currentUserId) {
              // Jinak přiřaď k příslušnému uživateli
              if (!unreadPerUser[senderId]) {
                unreadPerUser[senderId] = [];
              }
              unreadPerUser[senderId].push(msg);
            }
          });
          setUnreadByUser(unreadPerUser);
          
          console.log('✅ Initial state set:', {
            unreadCount: unreadMessages.length,
            unreadPerUser
          });
        }
      } catch (error) {
        console.error('❌ Error in initial unread check:', error);
      }
    };
    
    // Počáteční inicializace
    initializeUnreadMessages();
    
    // Funkce pro nastavení dynamického intervalu
    const setupPolling = () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
      
      // Dynamický interval: 15s když je chat otevřený, 45s když je zavřený
      const interval = isOpenRef.current ? 15000 : 45000;
      
      pollInterval.current = setInterval(() => {
        updateOnlineStatus();
        checkNewMessages();
        
        // Pokud je chat otevřený a máme vybraného uživatele nebo skupinu, automaticky reload zpráv
        if (isOpenRef.current) {
          try {
            if (isDiscordChat && selectedDiscordChannel) {
              checkNewDiscordMessages(selectedDiscordChannel.id);
            } else if (isGroupChat) {
              loadGroupMessages();
            } else if (selectedUserRef.current?._id) {
              loadMessages(selectedUserRef.current._id);
            }
          } catch (error) {
            console.error('❌ Error during auto-refresh:', error);
          }
        }
      }, interval);
    };
    
    // Počáteční nastavení pollingu
    setupPolling();

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [currentUserId]); // Pouze currentUserId — interval se nerestartuje při přepínání uživatelů v chatu

  // Automatické načtení Discord serverů při otevření chatu
  useEffect(() => {
    if (isOpen && discordAuth && discordChannels.length === 0) {
      console.log('🔄 Auto-loading Discord guilds on chat open...');
      loadDiscordGuilds();
    }
  }, [isOpen, discordAuth]);

  const updateOnlineStatus = async () => {
    try {
      if (currentUserId && currentUserId !== 'online') {
        await fetch(`${API_URL}/users/online`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: currentUserId })
        });
      }

      const response = await fetch(`${API_URL}/users/online`);
      if (response.ok) {
        const onlineUserIds = await response.json();
        setOnlineUsers(new Set(onlineUserIds));
      }
    } catch (error) {
      setOnlineUsers(new Set(users.map(u => u._id)));
    }
  };

  const checkNewMessages = async () => {
    try {
      console.log('Checking new messages for user:', currentUserId);
      
      const response = await fetch(`${API_URL}/messages/unread/${currentUserId}`);
      if (response.ok) {
        const unreadMessages = await response.json();
        
        const newUnreadCount = unreadMessages.length;
        const previousUnreadCount = unreadCount;
        
        // Seskup nepřečtené zprávy podle odesílatele nebo skupiny
        const unreadPerUser = {};
        unreadMessages.forEach(msg => {
          const senderId = typeof msg.senderId === 'object' ? msg.senderId._id : msg.senderId;
          
          // Zpracuj skupinové zprávy správně
          if (msg.isGroupMessage) {
            if (!unreadPerUser['group']) {
              unreadPerUser['group'] = [];
            }
            unreadPerUser['group'].push(msg);
          } else if (senderId !== currentUserId) {
            // Ignore vlastní zprávy
            if (!unreadPerUser[senderId]) {
              unreadPerUser[senderId] = [];
            }
            unreadPerUser[senderId].push(msg);
          }
        });
        
        // Show notification if there are new unread messages
        if (newUnreadCount > previousUnreadCount) {
          const newMessagesCount = newUnreadCount - previousUnreadCount;
          if (Object.keys(unreadPerUser).includes('group')) {
            showNotification(
              'Nové skupinové zprávy', 
              `Máte ${newMessagesCount} nových zpráv ve skupinovém chatu`
            );
          } else {
            const senderIds = Object.keys(unreadPerUser);
            const senderNames = senderIds.map(id => {
              const user = users.find(u => u._id === id);
              return user ? user.name : 'Neznámý';
            }).join(', ');
            showNotification(
              'Nová zpráva', 
              `${newMessagesCount} nových zpráv od: ${senderNames}`
            );
          }
        }
        
        // Update states
        setUnreadCount(newUnreadCount);
        setUnreadByUser(unreadPerUser);
        
        console.log('Set unread count to:', newUnreadCount, 'with breakdown:', unreadPerUser);
      } else {
        console.error('Failed to fetch unread messages:', response.status);
      }
    } catch (error) {
      console.error('Error checking new messages:', error);
    }
  };

  // Show browser notification for new messages
  const showNotification = (title, body, icon = '💬') => {
    if ('Notification' in window && Notification.permission === 'granted') {
      // Only show notification if chat is closed or window is not focused
      if (!isOpen || document.hidden) {
        new Notification(title, {
          body: body,
          icon: icon,
          badge: icon,
          tag: 'crm-chat-notification'
        });
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Delayed scroll to bottom - užitečné po načtení zpráv
  const scrollToBottomDelayed = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Event listener pro otevření AI chatu z jiných komponent
  useEffect(() => {
    const handleOpenAIChat = (event) => {
      const { message, autoSend } = event.detail;
      
      // Otevři chat
      setIsOpen(true);
      
      // Přepni na AI chat
      setIsAIChat(true);
      setIsGroupChat(false);
      setSelectedUser(null);
      
      // Nastav zprávu
      setNewMessage(message);
      
      // Pokud je autoSend true, automaticky odešli zprávu
      if (autoSend && message.trim()) {
        console.log('🤖 handleOpenAIChat: Auto-sending message:', message.trim());
        setTimeout(() => {
          // Vytvoř uživatelskou zprávu
          const userMessage = {
            _id: `ai_user_${Date.now()}`,
            senderId: currentUserId,
            text: message.trim(),
            timestamp: new Date(),
            isAI: false
          };

          // Vytvoř typing indikátor
          const typingMessage = {
            _id: `ai_typing_${Date.now()}`,
            senderId: 'ai',
            text: 'Generování odpovědi...',
            timestamp: new Date(),
            isAI: true,
            isTyping: true
          };

          // Přidej obě zprávy najednou do aiMessages i messages
          const messagesWithTyping = [...aiMessages, userMessage, typingMessage];
          console.log('🤖 Adding typing message, total messages:', messagesWithTyping.length);
          console.log('🤖 Typing message added:', typingMessage);
          setAiMessages(messagesWithTyping);
          setMessages(messagesWithTyping);

          // Vynuluj input a nastav typing
          setNewMessage('');
          setAiTyping(true);

          // Získej AI odpověď
          getAIResponse(message.trim()).then(aiResponse => {
            console.log('🤖 Got AI response, removing typing indicator');
            setAiTyping(false);
            const aiMessage = {
              _id: `ai_bot_${Date.now()}`,
              senderId: 'ai',
              text: aiResponse,
              timestamp: new Date(),
              isAI: true
            };

            // Odstraň typing indikátor a přidej AI odpověď
            const withoutTyping = messagesWithTyping.filter(msg => !msg.isTyping);
            const finalMessages = [...withoutTyping, aiMessage];
            console.log('🤖 Final messages after AI response:', finalMessages.length);
            setAiMessages(finalMessages);
            setMessages(finalMessages);
          }).catch(error => {
            console.error('🤖 AI response error:', error);
            setAiTyping(false);
            const errorMessage = {
              _id: `ai_error_${Date.now()}`,
              senderId: 'ai',
              text: 'Omlouvám se, momentálně nemohu odpovědět. Zkuste to prosím později.',
              timestamp: new Date(),
              isAI: true
            };
            
            // Odstraň typing indikátor a přidej chybovou zprávu
            const withoutTyping = messagesWithTyping.filter(msg => !msg.isTyping);
            const finalMessages = [...withoutTyping, errorMessage];
            setAiMessages(finalMessages);
            setMessages(finalMessages);
          });
        }, 100);
      }
    };

    window.addEventListener('openAIChat', handleOpenAIChat);
    
    return () => {
      window.removeEventListener('openAIChat', handleOpenAIChat);
    };
  }, [currentUserId]);

  const loadMessages = async (userId) => {
    setIsLoadingMessages(true);
    try {
      const response = await fetch(`${API_URL}/messages/${currentUserId}/${userId}`);
      if (response.ok) {
        const chatMessages = await response.json();
        setMessages(chatMessages);
        
        const markReadResponse = await fetch(`${API_URL}/messages/mark-read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            userId: currentUserId, 
            senderId: userId 
          })
        });
        
        // Pokud se nějaké zprávy označily jako přečtené, znovu načti zprávy
        if (markReadResponse.ok) {
          const markReadResult = await markReadResponse.json();
          if (markReadResult.modifiedCount > 0) {
            console.log(`Marked ${markReadResult.modifiedCount} messages as read`);
            // Znovu načti zprávy pro aktualizovaný read status
            const updatedResponse = await fetch(`${API_URL}/messages/${currentUserId}/${userId}`);
            if (updatedResponse.ok) {
              const updatedMessages = await updatedResponse.json();
              setMessages(updatedMessages);
            }
            
            // Aktualizuj stav nepřečtených zpráv - odstraň badge pro tohoto uživatele
            setUnreadByUser(prev => {
              const updated = { ...prev };
              delete updated[userId];
              return updated;
            });
            
            // Znovu načti celkový počet nepřečtených zpráv
            checkNewMessages();
          }
        }
        
        checkNewMessages();
        
        // Zaručím scroll na spodek po načtení všech zpráv
        scrollToBottomDelayed();
      } else {
        setMessages([]);
      }
    } catch (error) {
      setMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const loadGroupMessages = async () => {
    try {
      const response = await fetch(`${API_URL}/messages/group`);
      if (response.ok) {
        const groupMessages = await response.json();
        setMessages(groupMessages);
        
        // Označit skupinové zprávy jako přečtené
        const markReadResponse = await fetch(`${API_URL}/messages/mark-group-read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: currentUserId })
        });
        
        // Pokud se nějaké zprávy označily jako přečtené, znovu načti zprávy
        if (markReadResponse.ok) {
          const markReadResult = await markReadResponse.json();
          if (markReadResult.modifiedCount > 0) {
            console.log(`Marked ${markReadResult.modifiedCount} group messages as read`);
            // Znovu načti zprávy pro aktualizovaný read status
            const updatedResponse = await fetch(`${API_URL}/messages/group`);
            if (updatedResponse.ok) {
              const updatedMessages = await updatedResponse.json();
              setMessages(updatedMessages);
            }
            
            // Aktualizuj stav nepřečtených zpráv - odstraň badge pro skupinu
            setUnreadByUser(prev => {
              const updated = { ...prev };
              delete updated['group'];
              return updated;
            });
            
            // Znovu načti celkový počet nepřečtených zpráv
            checkNewMessages();
          }
        }
        
        checkNewMessages();
        
        // Zaručím scroll na spodek po načtení skupinových zpráv
        scrollToBottomDelayed();
      } else {
        setMessages([]);
      }
    } catch (error) {
      setMessages([]);
    }
  };

  const handleUserSelect = async (e, user) => {
    e.preventDefault();
    e.stopPropagation();
    setIsGroupChat(false);
    setIsAIChat(false);
    setSelectedUser(user);
    
    // Okamžitě skryj badge pro tohoto uživatele
    setUnreadByUser(prev => {
      const updated = { ...prev };
      delete updated[user._id];
      return updated;
    });
    
    await loadMessages(user._id);
    
    // Na mobilu přepni na chat view
    if (isMobile) {
      setShowChatView(true);
    }
    
    // Extra scroll po výběru uživatele pro jistotu
    scrollToBottomDelayed();
  };

  const handleGroupChatSelect = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsGroupChat(true);
    setSelectedUser(null);
    setIsAIChat(false);
    
    // Okamžitě skryj badge pro skupinový chat
    setUnreadByUser(prev => {
      const updated = { ...prev };
      delete updated['group'];
      return updated;
    });
    
    await loadGroupMessages();
    
    // Na mobilu přepni na chat view
    if (isMobile) {
      setShowChatView(true);
    }
    
    // Extra scroll po výběru skupinového chatu pro jistotu
    scrollToBottomDelayed();
  };

  const handleAIChatSelect = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🤖 AI Chat button clicked!');
    console.log('🤖 Setting isAIChat to true, isMobile:', isMobile);
    
    setIsAIChat(true);
    setIsGroupChat(false);
    setIsDiscordChat(false);
    setSelectedUser(null);
    setSelectedDiscordChannel(null);
    setMessages(aiMessages);
    
    // Na mobilu přepni na chat view
    if (isMobile) {
      console.log('🤖 Mobile detected, setting showChatView to true');
      setShowChatView(true);
    }
    
    // Scroll na spodek AI chatu
    scrollToBottomDelayed();
    
    console.log('🤖 AI Chat selection complete');
  };

  // Funkce pro získání AI odpovědi - výhradně OpenAI GPT (jako GitHub Copilot)
  const getAIResponse = async (userMessage) => {
    console.log('🤖 getAIResponse called with:', userMessage);
    
    try {
      console.log('� Calling OpenAI GPT backend (GitHub Copilot quality)...');
      
      // Připrav historii chatu pro AI (bez typing zpráv)
      const chatHistory = aiMessages.filter(msg => !msg.isTyping).map(msg => ({
        text: msg.text,
        isAI: msg.isAI
      }));
      
      console.log(`📝 Sending chat history with ${chatHistory.length} messages`);
      
      // Zavolej backend AI endpoint s OpenAI GPT a historií
      const response = await fetch(`${API_URL}/ai-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: userMessage,
          chatHistory: chatHistory
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🤖 AI API response:', data);
        
        // Zkontroluj jestli byla použita vyhledávání
        if (data.hasSearchResults) {
          console.log(`🔍 Search results found: ${data.searchResultsCount} results for query: "${data.searchQuery}"`);
        }
        
        // Zkontroluj jestli AI používá vlastní data
        if (data.hasCustomData) {
          console.log('📚 AI is using custom data from AI Data Manager');
        }
        
        // Zkontroluj jaká AI byla použita
        if (data.usedOpenAI) {
          console.log('🚀 OpenAI GPT was used (GitHub Copilot quality)');
        } else {
          console.log('⚠️ OpenAI not available - check API key configuration');
        }
        
        return data.response;
      } else {
        throw new Error('AI API error');
      }
    } catch (error) {
      console.error('🔴 Error calling OpenAI GPT API:', error);
      
      // Jen error zpráva - žádné fallback AI modely
      return "🔴 Omlouvám se, OpenAI GPT momentálně není dostupné.\n\n🤖 Používám výhradně OpenAI GPT pro nejvyšší kvalitu odpovědí (stejnou jako GitHub Copilot).\n\n🔧 Zkontrolujte:\n• Internetové připojení\n• Backend server běží\n• OpenAI API klíč je nakonfigurován\n\n� Zkuste to znovu za chvilku.";
    }
  };

  // Funkce pro návrat zpět na seznam uživatelů (pouze pro mobil)
  const handleBackToUserList = () => {
    setShowChatView(false);
    setSelectedUser(null);
    setIsGroupChat(false);
    setIsAIChat(false);
    setIsDiscordChat(false);
    setSelectedDiscordChannel(null);
    setMessages([]);
  };

  // Funkce pro vymazání AI chatu a start nového
  const handleNewAIChat = () => {
    if (window.confirm('Opravdu chcete vymazat celou konverzaci s AI a začít nový chat?')) {
      setAiMessages([]);
      setMessages([]);
      setNewMessage('');
      console.log('🗑️ AI chat cleared - starting new conversation');
    }
  };

  // === DISCORD FUNKCE ===
  
  // Funkce pro přihlášení k Discord přes OAuth
  const handleDiscordLogin = async () => {
    try {
      // Získej Discord OAuth URL z backendu
      const response = await fetch(`${API_URL}/discord/auth-url`);
      const data = await response.json();
      
      if (!data.authUrl) {
        throw new Error('Failed to get Discord auth URL');
      }

      // Otevři Discord OAuth v novém okně
      const width = 500;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const authWindow = window.open(
        data.authUrl,
        'Discord OAuth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Poslouchej na callback message z OAuth okna
      const handleMessage = async (event) => {
        if (event.data.type === 'discord-oauth-callback' && event.data.code) {
          console.log('📥 Received Discord OAuth code');
          
          // Zavři OAuth okno
          if (authWindow) {
            authWindow.close();
          }
          
          // Pošli code na backend pro výměnu za token
          const callbackResponse = await fetch(`${API_URL}/discord/callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              code: event.data.code,
              userId: currentUserId
            })
          });

          const callbackData = await callbackResponse.json();
          
          if (callbackData.success) {
            console.log('✅ Discord authentication successful');
            
            // Ulož auth info do stavu a localStorage
            const authData = {
              authenticated: true,
              discordUser: callbackData.discordUser,
              timestamp: new Date().toISOString()
            };
            setDiscordAuth(authData);
            localStorage.setItem('discordAuth', JSON.stringify(authData));
            
            setShowDiscordLogin(false);
            
            // Načti Discord servery
            await loadDiscordGuilds();
          } else {
            throw new Error('Failed to authenticate with Discord');
          }
        }
      };

      window.addEventListener('message', handleMessage);
      
      // Vyčisti event listener po 5 minutách
      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
      }, 5 * 60 * 1000);
      
    } catch (error) {
      console.error('❌ Error during Discord login:', error);
      alert('Chyba při přihlašování k Discord. Zkuste to znovu.');
    }
  };

  // Funkce pro odhlášení z Discord
  const handleDiscordLogout = async () => {
    if (window.confirm('Opravdu se chcete odhlásit z Discord?')) {
      try {
        await fetch(`${API_URL}/discord/auth/${currentUserId}`, {
          method: 'DELETE'
        });
        
        setDiscordAuth(null);
        setDiscordChannels([]);
        setDiscordMessages([]);
        setSelectedDiscordChannel(null);
        setIsDiscordChat(false);
        
        console.log('✅ Disconnected from Discord');
      } catch (error) {
        console.error('❌ Error during Discord logout:', error);
      }
    }
  };

  // Načtení Discord serverů (guilds)
  const loadDiscordGuilds = async () => {
    setIsLoadingDiscord(true);
    try {
      console.log('📡 Loading Discord guilds...');
      
      const response = await fetch(`${API_URL}/discord/guilds/${currentUserId}`);
      const data = await response.json();
      
      if (data.guilds) {
        // Najdi server "Vyjednáme"
        const vyjedname = data.guilds.find(guild => 
          guild.name.toLowerCase().includes('vyjedname') || 
          guild.name.toLowerCase().includes('vyjednáme')
        );
        
        if (vyjedname) {
          console.log('✅ Auto-selecting Vyjednáme server:', vyjedname.name);
          // Automaticky načti kanály serveru Vyjednáme
          await loadDiscordChannels(vyjedname.id);
        } else {
          // Pokud server Vyjednáme není nalezen, zobraz všechny servery
          console.warn('⚠️ Server Vyjednáme nebyl nalezen, zobrazuji všechny servery');
          setDiscordChannels(data.guilds.map(guild => ({
            id: guild.id,
            name: guild.name,
            type: 'guild',
            icon: guild.iconUrl
          })));
        }
        console.log('✅ Discord guilds loaded:', data.guilds.length);
      }
    } catch (error) {
      console.error('❌ Error loading Discord guilds:', error);
      alert('Chyba při načítání Discord serverů');
    } finally {
      setIsLoadingDiscord(false);
    }
  };

  // Načtení kanálů v rámci serveru
  const loadDiscordChannels = async (guildId) => {
    setIsLoadingDiscord(true);
    try {
      console.log('📡 Loading Discord channels for guild:', guildId, 'for user:', currentUserId);
      
      const response = await fetch(`${API_URL}/discord/guilds/${guildId}/channels?userId=${currentUserId}`);
      const data = await response.json();
      
      // Pokud bot nemá přístup na server
      if (data.botHasAccess === false) {
        console.warn('⚠️ Bot nemá přístup na server:', guildId);
        setDiscordChannels([]);
        setDiscordCategories([]);
        setDiscordChannelsByCategory({});
        setDiscordUncategorized([]);
        
        const botInviteUrl = `https://discord.com/api/oauth2/authorize?client_id=${process.env.REACT_APP_DISCORD_CLIENT_ID || '1302937669078040668'}&permissions=3072&scope=bot`;
        
        if (window.confirm(
          '🤖 Bot nemá přístup na tento Discord server.\n\n' +
          'Pro zobrazení kanálů musíš nejdřív pozvat bota na server.\n\n' +
          'Klikni OK pro otevření odkazu na pozvání bota.'
        )) {
          window.open(botInviteUrl, '_blank');
        }
        return;
      }
      
      if (data.channels) {
        setDiscordChannels(data.channels);
        setDiscordCategories(data.categories || []);
        setDiscordChannelsByCategory(data.channelsByCategory || {});
        setDiscordUncategorized(data.uncategorizedChannels || []);
        
        // Automaticky rozbal všechny kategorie
        if (data.categories) {
          setExpandedCategories(new Set(data.categories.map(cat => cat.id)));
        }
        
        console.log('✅ Discord channels loaded:', data.channels.length);
        console.log('✅ Categories:', data.categories?.length || 0);
      }
    } catch (error) {
      console.error('❌ Error loading Discord channels:', error);
      alert('Chyba při načítání Discord kanálů');
    } finally {
      setIsLoadingDiscord(false);
    }
  };

  // Auto-load guilds when chat is opened and discordAuth exists
  useEffect(() => {
    // Pokud není otevřen chat nebo není discordAuth, nic nedělej
    if (!isOpen || !discordAuth) return;

    let cancelled = false;

    (async () => {
      try {
        console.log('🔁 Auto-loading Discord guilds because chat opened and auth exists');
        await loadDiscordGuilds();
        if (!cancelled) {
          // pokud se načetly servery, necháme uživatele vybrat server
          console.log('🔁 Auto-loaded guilds successfully');
        }
      } catch (err) {
        console.error('❌ Auto-load guilds failed:', err);
        // Pokud došlo k chybě autorizace, smaž uložené přihlášení
        try {
          localStorage.removeItem('discordAuth');
        } catch (e) {}
        setDiscordAuth(null);
        alert('Discord přihlášení je neplatné nebo expirované. Prosím přihlaste se znovu.');
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, discordAuth]);

  // Persist discordAuth to localStorage so non-incognito sessions keep auth
  useEffect(() => {
    try {
      if (discordAuth) {
        localStorage.setItem('discordAuth', JSON.stringify(discordAuth));
      } else {
        localStorage.removeItem('discordAuth');
      }
    } catch (e) {
      console.warn('⚠️ Could not persist discordAuth to localStorage', e);
    }
  }, [discordAuth]);

  // Načtení zpráv z Discord kanálu
  const loadDiscordMessages = async (channelId) => {
    setIsLoadingMessages(true);
    try {
      console.log('📡 Loading Discord messages for channel:', channelId);
      
      const response = await fetch(`${API_URL}/discord/channels/${channelId}/messages?limit=50`);
      const data = await response.json();
      
      if (data.messages) {
        console.log('📥 Raw Discord messages:', data.messages);
        
        // Debug první zprávy
        if (data.messages.length > 0) {
          console.log('🔍 First message sample:', {
            content: data.messages[0].content,
            attachments: data.messages[0].attachments,
            embeds: data.messages[0].embeds,
            author: data.messages[0].author
          });
        }
        
        setDiscordMessages(data.messages);
        setMessages(data.messages.map(msg => {
          // Sestavení textu zprávy
          let messageText = msg.content || '';
          
          // Normalizace attachments na array
          let attachmentsArray = [];
          if (msg.attachments) {
            if (Array.isArray(msg.attachments)) {
              attachmentsArray = msg.attachments;
            } else if (typeof msg.attachments === 'object') {
              attachmentsArray = Object.values(msg.attachments);
            }
          }
          
          // Přidej informaci o přílohách
          if (attachmentsArray.length > 0) {
            const attachmentInfo = attachmentsArray.map(att => {
              if (att.content_type?.startsWith('image/')) {
                return `🖼️ ${att.filename || 'image'}`;
              }
              return `📎 ${att.filename || 'file'}`;
            }).join('\n');
            messageText = messageText ? `${messageText}\n${attachmentInfo}` : attachmentInfo;
          }
          
          // Normalizace embeds na array
          let embedsArray = [];
          if (msg.embeds) {
            if (Array.isArray(msg.embeds)) {
              embedsArray = msg.embeds;
            } else if (typeof msg.embeds === 'object') {
              embedsArray = [msg.embeds];
            }
          }
          
          // Přidej informaci o embedech
          if (embedsArray.length > 0) {
            const embedInfo = embedsArray.map(embed => 
              embed.title || embed.description || '🔗 Embed'
            ).join('\n');
            messageText = messageText ? `${messageText}\n${embedInfo}` : embedInfo;
          }
          
          // Pokud stále žádný text, označ jako prázdnou
          if (!messageText.trim()) {
            console.warn('⚠️ Message has no content/attachments/embeds:', msg.id);
            messageText = '(prázdná zpráva)';
          }
          
          return {
            _id: msg.id,
            text: messageText,
            sender: { 
              name: msg.author?.displayName || msg.author?.username || 'Neznámý uživatel',
              _id: msg.author?.id 
            },
            createdAt: msg.timestamp,
            isDiscord: true,
            attachments: attachmentsArray,
            embeds: embedsArray
          };
        }));
        
        console.log('✅ Discord messages loaded:', data.messages.length);
        scrollToBottomDelayed();
      }
    } catch (error) {
      console.error('❌ Error loading Discord messages:', error);
      alert('Chyba při načítání Discord zpráv');
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Kontrola nových Discord zpráv pro notifikace
  const checkNewDiscordMessages = async (channelId) => {
    try {
      const response = await fetch(`${API_URL}/discord/channels/${channelId}/messages?limit=10`);
      const data = await response.json();
      
      if (data.messages && data.messages.length > 0) {
        const currentMessageIds = new Set(discordMessages.map(m => m.id));
        const newMessages = data.messages.filter(m => !currentMessageIds.has(m.id));
        
        // Pokud jsou nové zprávy a chat není ve focus nebo je zavřený
        if (newMessages.length > 0 && (!isOpen || !document.hasFocus())) {
          const channelName = selectedDiscordChannel?.name || 'Discord';
          const latestMessage = newMessages[0];
          const authorName = latestMessage.author?.displayName || latestMessage.author?.username || 'Neznámý';
          const messagePreview = latestMessage.content?.substring(0, 100) || '(příloha)';
          
          showNotification(
            `#${channelName}`,
            `${authorName}: ${messagePreview}`,
            '💬'
          );
          
          // Aktualizuj zprávy
          setDiscordMessages(data.messages);
          setMessages(data.messages.map(msg => {
            let messageText = msg.content || '';
            
            let attachmentsArray = [];
            if (msg.attachments) {
              if (Array.isArray(msg.attachments)) {
                attachmentsArray = msg.attachments;
              } else if (typeof msg.attachments === 'object') {
                attachmentsArray = Object.values(msg.attachments);
              }
            }
            
            if (attachmentsArray.length > 0) {
              const attachmentInfo = attachmentsArray.map(att => {
                if (att.content_type?.startsWith('image/')) {
                  return `🖼️ ${att.filename || 'image'}`;
                }
                return `📎 ${att.filename || 'file'}`;
              }).join('\n');
              messageText = messageText ? `${messageText}\n${attachmentInfo}` : attachmentInfo;
            }
            
            let embedsArray = [];
            if (msg.embeds) {
              if (Array.isArray(msg.embeds)) {
                embedsArray = msg.embeds;
              } else if (typeof msg.embeds === 'object') {
                embedsArray = [msg.embeds];
              }
            }
            
            if (embedsArray.length > 0) {
              const embedInfo = embedsArray.map(embed => 
                embed.title || embed.description || '🔗 Embed'
              ).join('\n');
              messageText = messageText ? `${messageText}\n${embedInfo}` : embedInfo;
            }
            
            if (!messageText.trim()) {
              messageText = '(prázdná zpráva)';
            }
            
            return {
              _id: msg.id,
              text: messageText,
              sender: { 
                name: msg.author?.displayName || msg.author?.username || 'Neznámý uživatel',
                _id: msg.author?.id 
              },
              createdAt: msg.timestamp,
              isDiscord: true,
              attachments: attachmentsArray,
              embeds: embedsArray
            };
          }));
        }
      }
    } catch (error) {
      console.error('❌ Error checking Discord messages:', error);
    }
  };

  // Odeslání zprávy do Discord
  // Handler pro výběr souboru
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Zkontroluj velikost (max 25MB)
    if (file.size > 25 * 1024 * 1024) {
      alert('Soubor je příliš velký! Maximum je 25MB.');
      return;
    }

    // Zkontroluj typ
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      alert('Nepodporovaný formát! Podporované formáty: JPEG, PNG, GIF, WebP, MP4, WebM, MOV');
      return;
    }

    setSelectedMediaFile(file);

    // Vytvoř preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setMediaPreview({
        url: e.target.result,
        type: file.type,
        name: file.name
      });
    };
    reader.readAsDataURL(file);
  };

  // Zrušení výběru média
  const cancelMediaSelection = () => {
    setSelectedMediaFile(null);
    setMediaPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const sendDiscordMessage = async (messageText) => {
    if (!discordAuth || !selectedDiscordChannel) {
      alert('Není připojení k Discord');
      return;
    }

    try {
      console.log('📤 Sending message to Discord...');
      
      // Pokud je vybraný soubor, pošli ho
      if (selectedMediaFile) {
        const formData = new FormData();
        formData.append('file', selectedMediaFile);
        formData.append('content', messageText);
        formData.append('userId', currentUserId);

        const response = await fetch(`${API_URL}/discord/channels/${selectedDiscordChannel.id}/messages/media`, {
          method: 'POST',
          body: formData
        });

        const data = await response.json();

        if (data.message) {
          console.log('✅ Media message sent to Discord');
          
          // Přidej zprávu lokálně
          const newMsg = {
            _id: data.message.id,
            text: data.message.content,
            sender: { 
              name: users?.find(u => u._id === currentUserId)?.name || 'You',
              _id: currentUserId 
            },
            createdAt: data.message.timestamp,
            isDiscord: true,
            attachments: data.message.attachments || []
          };
          
          setMessages(prev => [...prev, newMsg]);
          cancelMediaSelection();
          scrollToBottomDelayed();
        } else {
          throw new Error('Failed to send media message');
        }
      } else {
        // Pošli pouze textovou zprávu
        const response = await fetch(`${API_URL}/discord/channels/${selectedDiscordChannel.id}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: messageText,
            userId: currentUserId
          })
        });

        const data = await response.json();

        if (data.message) {
          console.log('✅ Message sent to Discord');
          
          // Přidej zprávu lokálně
          const newMsg = {
            _id: data.message.id,
            text: data.message.content,
            sender: { 
              name: users?.find(u => u._id === currentUserId)?.name || 'You',
              _id: currentUserId 
            },
            createdAt: data.message.timestamp,
            isDiscord: true
          };
          
          setMessages(prev => [...prev, newMsg]);
          scrollToBottomDelayed();
        } else {
          throw new Error('Failed to send message');
        }
      }
    } catch (error) {
      console.error('❌ Error sending Discord message:', error);
      alert('Chyba při odesílání zprávy do Discord');
    }
  };

  // Funkce pro výběr Discord
  const handleDiscordSelect = async (e) => {
    e.preventDefault();
    
    setSelectedUser(null);
    setIsGroupChat(false);
    setIsAIChat(false);
    setIsDiscordChat(true);
    setMessages([]);
    setShowChatView(true);
    
    // Zkontroluj Discord auth status
    try {
      const response = await fetch(`${API_URL}/discord/auth-status/${currentUserId}`);
      const data = await response.json();
      
      if (data.authenticated) {
        setDiscordAuth({
          authenticated: true,
          discordUser: data.discordUser
        });
        // Načti Discord servery
        await loadDiscordGuilds();
      } else {
        // Zobraz přihlašovací formulář
        setShowDiscordLogin(true);
      }
    } catch (error) {
      console.error('❌ Error checking Discord auth:', error);
      setShowDiscordLogin(true);
    }
  };

  // Funkce pro výběr Discord serveru nebo kanálu
  // Toggle collapse/expand kategorie
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleDiscordChannelSelect = (e, channel) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (channel.type === 'guild') {
      // Pokud je to server (guild), načti jeho kanály
      loadDiscordChannels(channel.id);
    } else {
      // Pokud je to kanál, načti zprávy
      setIsDiscordChat(true);
      setIsGroupChat(false);
      setIsAIChat(false);
      setSelectedUser(null);
      setSelectedDiscordChannel(channel);
      loadDiscordMessages(channel.id);
      
      // Na mobilu přepni na chat view
      if (isMobile) {
        setShowChatView(true);
      }
    }
  };

  // Funkce pro opravu AI odpovědi
  const handleCorrectAI = async (messageId, userQuestion, wrongAnswer, correctAnswer) => {
    console.log('🔧 Correcting AI response...');
    
    try {
      // Vytvoř záznam opravy pro AI Data Manager
      const correctionData = `
OPRAVA AI ODPOVĚDI (${new Date().toLocaleString('cs-CZ')}):
Dotaz uživatele: ${userQuestion}
Špatná odpověď AI: ${wrongAnswer}
Správná odpověď: ${correctAnswer}

---
`;

      // Zavolej backend pro přidání opravy do AI dat
      const response = await fetch(`${API_URL}/ai-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          data: correctionData,
          isCorrection: true
        })
      });

      if (response.ok) {
        console.log('✅ AI correction saved to database');
        
        // Aktualizuj AI zprávu s novou správnou odpovědí
        const updatedMessages = aiMessages.map(msg => 
          msg._id === messageId 
            ? { ...msg, text: correctAnswer, corrected: true }
            : msg
        );
        
        setAiMessages(updatedMessages);
        setMessages(updatedMessages);
        
        // Resetuj stav opravy
        setCorrectingAI(null);
        setCorrectionText('');
        
        alert('✅ AI odpověď byla opravena a uložena do databáze pro budoucí učení!');
      } else {
        throw new Error('Failed to save correction');
      }
    } catch (error) {
      console.error('❌ Error correcting AI:', error);
      alert('❌ Chyba při ukládání opravy. Zkuste to znovu.');
    }
  };

  const addEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojis(false);
  };

  // Funkce pro režim mazání zpráv (pouze admin)
  const toggleDeleteMode = () => {
    setDeleteMode(!deleteMode);
    setSelectedMessages(new Set());
  };

  const toggleMessageSelection = (messageId) => {
    const newSelected = new Set(selectedMessages);
    if (newSelected.has(messageId)) {
      newSelected.delete(messageId);
    } else {
      newSelected.add(messageId);
    }
    setSelectedMessages(newSelected);
  };

  const deleteSelectedMessages = async () => {
    if (selectedMessages.size === 0) return;
    
    try {
      const response = await fetch(`${API_URL}/messages/delete-multiple`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messageIds: Array.from(selectedMessages)
        })
      });

      if (response.ok) {
        // Reload messages after deletion
        if (isGroupChat) {
          await loadGroupMessages();
        } else if (selectedUser) {
          await loadMessages(selectedUser._id);
        }
        
        // Exit delete mode
        setDeleteMode(false);
        setSelectedMessages(new Set());
      }
    } catch (error) {
      console.error('Error deleting messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    if (!isGroupChat && !selectedUser && !isAIChat && !isDiscordChat) return;

    console.log('=== SENDING MESSAGE DEBUG ===');
    console.log('currentUserId (sender):', currentUserId, 'type:', typeof currentUserId);
    console.log('isGroupChat:', isGroupChat);
    console.log('isAIChat:', isAIChat);
    console.log('isDiscordChat:', isDiscordChat);
    console.log('selectedUser:', selectedUser);
    console.log('message text:', newMessage.trim());

    // Discord Chat logika
    if (isDiscordChat && selectedDiscordChannel) {
      await sendDiscordMessage(newMessage.trim());
      setNewMessage('');
      return;
    }

    // AI Chat logika
    if (isAIChat) {
      console.log('🤖 AI Chat: Processing message');
      const userMessage = {
        _id: `ai_user_${Date.now()}`,
        senderId: currentUserId,
        text: newMessage.trim(),
        timestamp: new Date(),
        isAI: false
      };

      // Přidej uživatelskou zprávu
      const updatedMessages = [...aiMessages, userMessage];
      setAiMessages(updatedMessages);
      setMessages(updatedMessages);
      setNewMessage('');

      console.log('🤖 AI Chat: User message added, getting AI response...');
      
      // Scroll po přidání uživatelské zprávy
      scrollToBottomDelayed();

      // Přidej indikátor "Generování odpovědi..."
      setAiTyping(true);
      const typingMessage = {
        _id: `ai_typing_${Date.now()}`,
        senderId: 'ai',
        text: 'Generování odpovědi...',
        timestamp: new Date(),
        isAI: true,
        isTyping: true
      };
      
      const messagesWithTyping = [...updatedMessages, typingMessage];
      setMessages(messagesWithTyping);

      try {
        // Simulace AI odpovědi (později nahradíme skutečným AI API)
        const aiResponse = await getAIResponse(newMessage.trim());
        console.log('🤖 AI Chat: Got response:', aiResponse);
        
        setAiTyping(false);
        const aiMessage = {
          _id: `ai_bot_${Date.now()}`,
          senderId: 'ai',
          text: aiResponse,
          timestamp: new Date(),
          isAI: true
        };

        const finalMessages = [...updatedMessages, aiMessage];
        setAiMessages(finalMessages);
        setMessages(finalMessages);
        console.log('🤖 AI Chat: AI response added to messages');
        
        // Scroll na spodek po AI odpovědi
        scrollToBottomDelayed();
      } catch (error) {
        console.error('🤖 AI response error:', error);
        setAiTyping(false);
        const errorMessage = {
          _id: `ai_error_${Date.now()}`,
          senderId: 'ai',
          text: 'Omlouvám se, momentálně nemohu odpovědět. Zkuste to prosím později.',
          timestamp: new Date(),
          isAI: true
        };
        
        const finalMessages = [...updatedMessages, errorMessage];
        setAiMessages(finalMessages);
        setMessages(finalMessages);
        
        // Scroll na spodek i pro error zprávy
        scrollToBottomDelayed();
      }
      return;
    }

    const messageData = isGroupChat ? {
      senderId: currentUserId,
      text: newMessage.trim(),
      isGroupMessage: true
    } : {
      senderId: currentUserId,
      receiverId: selectedUser._id,
      text: newMessage.trim()
    };

    console.log('messageData being sent:', messageData);

    try {
      const response = await fetch(`${API_URL}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(messageData)
      });
      
      if (response.ok) {
        const savedMessage = await response.json();
        console.log('Message saved from server:', savedMessage);
        
        // Extrahuj senderId z případně populovaného objektu
        const senderId = typeof savedMessage.senderId === 'object' ? savedMessage.senderId._id : savedMessage.senderId;
        console.log('extracted senderId from saved message:', senderId);
        
        setMessages(prev => [...prev, savedMessage]);
        setNewMessage('');
        
        // Okamžitě zkontroluj nové zprávy po odeslání
        checkNewMessages();
        // A ještě jednou za chvilku pro jistotu
        setTimeout(() => checkNewMessages(), 500);
        
        // Scroll na spodek po odeslání zprávy
        scrollToBottomDelayed();
      } else {
        throw new Error('Server error');
      }
    } catch (error) {
      console.error('Send message error:', error);
      
      // Fallback - local message
      const localMessage = {
        _id: `local_${Date.now()}_${Math.random()}`,
        senderId: currentUserId,
        receiverId: selectedUser._id,
        text: newMessage.trim(),
        timestamp: new Date(),
        read: false
      };
      console.log('Adding local message:', localMessage);
      setMessages(prev => [...prev, localMessage]);
      setNewMessage('');
      
      // I pro fallback zprávy zkontroluj nové zprávy
      checkNewMessages();
      
      // Scroll na spodek i pro fallback zprávy
      scrollToBottomDelayed();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Přidání CSS stylů pro formátování zpráv */}
      <style jsx>{`
        .formatted-text .bullet-point {
          margin: 1px 0;
          padding-left: 2px;
        }
        .formatted-text strong {
          font-weight: bold;
        }
        .formatted-text em {
          font-style: italic;
        }
      `}</style>

      {/* Chat button - na mobilu se skryje když je chat otevřený */}
      {!(isMobile && isOpen) && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="fixed bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white p-3 rounded-full shadow-lg transition-all relative"
          style={{ 
            zIndex: 10000,
            position: 'fixed',
            bottom: isMobile ? 'calc(20px + env(safe-area-inset-bottom))' : '44px', // Níže (48px - 4px)
            right: isMobile ? '20px' : '42px' // Více doprava (50px - 8px)
          }}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 dark:bg-red-400 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div 
          className={`fixed bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl flex flex-col ${
            isMobile ? 'inset-0 rounded-none chat-mobile-fullscreen' : ''
          }`}
          style={{ 
            zIndex: 9999,
            position: 'fixed',
            ...(isMobile ? {
              top: '0',
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh', // Celá výška bez safe area kalkulací zde
              maxWidth: '100vw',
              maxHeight: '100vh',
              borderRadius: 0,
              // iOS Safari specifické opravy
              WebkitOverflowScrolling: 'touch',
              overflowScrolling: 'touch',
              // Posun dolů kvůli iPhone zakulacené okraje a fotoaparát
              paddingTop: 'calc(env(safe-area-inset-top) + 20px)'
            } : {
              bottom: '90px',
              right: '20px',
              width: '1000px',
              height: '800px',
              maxWidth: 'calc(100vw - 40px)',
              maxHeight: 'calc(100vh - 130px)'
            })
          }}
        >
          {/* Header */}
          <div className={`bg-blue-600 dark:bg-blue-700 text-white p-3 flex justify-between items-center ${
            isMobile ? 'rounded-none' : 'rounded-t-lg'
          }`}>
            <div className="flex items-center gap-3">
              {/* Tlačítko zpět pouze na mobilu a pouze když je zobrazen chat */}
              {isMobile && showChatView && (
                <button
                  onClick={handleBackToUserList}
                  className="text-white hover:text-gray-200 text-lg"
                  title="Zpět na seznam"
                >
                  ←
                </button>
              )}
              <h3 className="font-semibold">
                {isMobile && !showChatView ? 'Team Chat' : 
                 isAIChat ? 'AI' :
                 isGroupChat ? 'Skupinový chat' : 
                 selectedUser ? `Chat s ${selectedUser.name}` : 'Team Chat'}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              {/* Tlačítko "Nový chat" pouze pro AI chat */}
              {isAIChat && aiMessages.length > 0 && (
                <button
                  onClick={handleNewAIChat}
                  className="text-white hover:text-gray-200 text-sm bg-purple-600 hover:bg-purple-700 px-2 py-1 rounded transition-colors"
                  title="Vymazat konverzaci a začít nový AI chat"
                >
                  🆕 Nový chat
                </button>
              )}

              {/* Admin controls for message deletion */}
              {isAdmin && (messages.length > 0) && (
                <>
                  {!deleteMode ? (
                    <button
                      onClick={toggleDeleteMode}
                      className="text-white hover:text-gray-200 text-sm bg-red-600 hover:bg-red-700 px-2 py-1 rounded transition-colors"
                      title="Smazat zprávy"
                    >
                      🗑️ Smazat
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-xs">
                        {selectedMessages.size > 0 ? `Označeno: ${selectedMessages.size}` : 'Označte zprávy'}
                      </span>
                      <button
                        onClick={deleteSelectedMessages}
                        disabled={selectedMessages.size === 0}
                        className="text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 px-2 py-1 rounded text-xs transition-colors"
                      >
                        ✓ Smazat ({selectedMessages.size})
                      </button>
                      <button
                        onClick={toggleDeleteMode}
                        className="text-white hover:text-gray-200 text-xs"
                      >
                        ✕ Zrušit
                      </button>
                    </div>
                  )}
                </>
              )}
              
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:text-gray-200 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Na mobilu: zobraz buď seznam uživatelů NEBO chat, ne oba najednou */}
            {isMobile ? (
              <>
                {/* Mobilní seznam uživatelů - zobraz pouze když není aktivní chat view */}
                {!showChatView && (
                  <div className="w-full bg-gray-50 dark:bg-gray-700 overflow-y-auto">
                    <div className="p-3">
                      {/* Komunikační služby */}
                      <div className="mb-4">
                        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">KOMUNIKACE</div>
                        
                        {/* AI Chat */}
                        <button
                          type="button"
                          onClick={(e) => handleAIChatSelect(e)}
                          className="w-full text-left p-3 rounded-lg bg-white dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors border border-gray-200 dark:border-gray-500 mb-3"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-purple-500 dark:bg-purple-600 rounded-full flex items-center justify-center text-lg font-semibold text-white">
                              🤖
                            </div>
                            <div className="flex-1">
                              <div className="text-base font-medium text-gray-900 dark:text-gray-100">AI Asistentka</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                Vyjednáme AI
                              </div>
                            </div>
                          </div>
                        </button>

                        {/* Discord - přihlášení nebo výběr serveru */}
                        {!discordAuth ? (
                          <button
                            type="button"
                            onClick={(e) => handleDiscordSelect(e)}
                            className="w-full text-left p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900 hover:bg-indigo-100 dark:hover:bg-indigo-800 transition-colors border-2 border-indigo-300 dark:border-indigo-500"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-indigo-600 dark:bg-indigo-700 rounded-full flex items-center justify-center text-lg font-semibold text-white">
                                💬
                              </div>
                              <div className="flex-1">
                                <div className="text-base font-medium text-gray-900 dark:text-gray-100">Připojit Discord</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  Klikněte pro autorizaci
                                </div>
                              </div>
                            </div>
                          </button>
                        ) : (
                          <>
                            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2 mt-4">
                              {discordChannels[0]?.type === 'guild' ? 'DISCORD SERVERY' : 'DISCORD KANÁLY'}
                            </div>
                            {discordChannels.length > 0 ? (
                              <>
                                {/* Pokud zobrazujeme guildy (servery) */}
                                {discordChannels[0]?.type === 'guild' ? (
                                  discordChannels.map(channel => (
                                    <button
                                      key={channel.id}
                                      type="button"
                                      onClick={(e) => handleDiscordChannelSelect(e, channel)}
                                      className="w-full text-left p-3 rounded-lg bg-white dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors border border-gray-200 dark:border-gray-500 mb-2"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-indigo-500 dark:bg-gray-500 rounded flex items-center justify-center text-lg font-semibold text-white">
                                          🖥️
                                        </div>
                                        <div className="flex-1">
                                          <div className="text-base font-medium text-gray-900 dark:text-gray-100">{channel.name}</div>
                                          <div className="text-sm text-gray-500 dark:text-gray-400">
                                            Discord server
                                          </div>
                                        </div>
                                      </div>
                                    </button>
                                  ))
                                ) : (
                                  <>
                                    {/* Nekategorizované kanály */}
                                    {discordUncategorized.map(channel => (
                                      <button
                                        key={channel.id}
                                        type="button"
                                        onClick={(e) => handleDiscordChannelSelect(e, channel)}
                                        className="w-full text-left p-2 rounded-lg bg-white dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors border border-gray-200 dark:border-gray-500 mb-1"
                                      >
                                        <div className="flex items-center gap-2">
                                          <span className="text-gray-500 dark:text-gray-400">#</span>
                                          <span className="text-sm text-gray-900 dark:text-gray-100">{channel.name}</span>
                                        </div>
                                      </button>
                                    ))}

                                    {/* Kategorie s kanály */}
                                    {discordCategories.map(category => (
                                      <div key={category.id} className="mb-2">
                                        <button
                                          type="button"
                                          onClick={() => toggleCategory(category.id)}
                                          className="w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                              {expandedCategories.has(category.id) ? '▼' : '▶'}
                                            </span>
                                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">
                                              {category.name}
                                            </span>
                                          </div>
                                        </button>
                                        
                                        {expandedCategories.has(category.id) && discordChannelsByCategory[category.id] && (
                                          <div className="ml-4 mt-1 space-y-1">
                                            {discordChannelsByCategory[category.id].map(channel => (
                                              <button
                                                key={channel.id}
                                                type="button"
                                                onClick={(e) => handleDiscordChannelSelect(e, channel)}
                                                className="w-full text-left p-2 rounded-lg bg-white dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors border border-gray-200 dark:border-gray-500"
                                              >
                                                <div className="flex items-center gap-2">
                                                  <span className="text-gray-500 dark:text-gray-400">#</span>
                                                  <span className="text-sm text-gray-900 dark:text-gray-100">{channel.name}</span>
                                                </div>
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}

                                    {/* Tlačítko zpět na servery */}
                                    <button
                                      type="button"
                                      onClick={() => loadDiscordGuilds()}
                                      className="w-full text-left p-3 rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-600 mt-2"
                                    >
                                      ← Zpět na servery
                                    </button>
                                  </>
                                )}
                              </>
                            ) : (
                              <div className="text-sm text-gray-500 dark:text-gray-400 p-3">Načítání...</div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Seznam uživatelů - SKRYTÝ */}
                      {false && hasPermission('viewAllUsers') && (
                        <div className="mt-4">
                          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">UŽIVATELÉ (DEAKTIVOVÁNO)</div>
                          <div className="space-y-2">
                            {visibleUsers.map(user => (
                            <button
                              key={user._id}
                              type="button"
                              onClick={(e) => handleUserSelect(e, user)}
                              className="w-full text-left p-3 rounded-lg bg-white dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors border border-gray-200 dark:border-gray-500"
                            >
                              <div className="flex items-center gap-3">
                                <div className="relative">
                                  <div className="w-12 h-12 bg-gray-300 dark:bg-gray-500 rounded-full flex items-center justify-center text-lg font-semibold text-gray-700 dark:text-gray-200">
                                    {user.name.charAt(0).toUpperCase()}
                                  </div>
                                  {onlineUsers.has(user._id) && (
                                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 dark:bg-green-400 rounded-full border-2 border-white dark:border-gray-600"></div>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="text-base font-medium text-gray-900 dark:text-gray-100">
                                    {user.name}
                                  </div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {onlineUsers.has(user._id) ? 'Online' : 'Offline'}
                                  </div>
                                </div>
                                
                                {/* Badge pro nepřečtené zprávy od tohoto uživatele */}
                                {unreadByUser[user._id] && unreadByUser[user._id].length > 0 && (
                                  <div className="bg-red-500 dark:bg-red-400 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                                    {unreadByUser[user._id].length > 99 ? '99+' : unreadByUser[user._id].length}
                                  </div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Mobilní chat view - zobraz pouze když je aktivní */}
                {showChatView && (selectedUser || isGroupChat || isAIChat || isDiscordChat) && (
                  <div className="w-full flex flex-col" style={{ height: '100%' }}>
                    {/* Messages */}
                    <div 
                      className="flex-1 overflow-y-auto custom-scrollbar p-4 bg-gray-50 dark:bg-gray-900" 
                      style={{
                        paddingTop: 'calc(16px + env(safe-area-inset-top))', // Safe area nahoře
                        paddingBottom: 'calc(150px + env(safe-area-inset-bottom))', // Místo pro input pole + safe area + URL panel
                        // iOS Safari scrolling optimalizace
                        WebkitOverflowScrolling: 'touch',
                        overflowScrolling: 'touch'
                      }}
                    >
                      {messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400 text-sm">
                          Zatím žádné zprávy
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {messages.map(message => {
                            const senderId = typeof message.senderId === 'object' ? message.senderId._id : message.senderId;
                            const isMyMessage = String(senderId) === String(currentUserId);
                            const isAIMessage = message.isAI || senderId === 'ai';
                            const sender = users.find(u => String(u._id) === String(senderId));
                            
                            return (
                              <div
                                key={message._id || `msg_${Date.now()}_${Math.random()}`}
                                className={`flex w-full ${isMyMessage ? 'justify-end' : 'justify-start'} ${
                                  deleteMode ? 'group cursor-pointer' : ''
                                }`}
                                onClick={() => deleteMode && toggleMessageSelection(message._id)}
                              >
                                <div className="relative">
                                  {deleteMode && isAdmin && !isAIMessage && (
                                    <div className="absolute -left-8 top-1/2 transform -translate-y-1/2">
                                      <input
                                        type="checkbox"
                                        checked={selectedMessages.has(message._id)}
                                        onChange={() => toggleMessageSelection(message._id)}
                                        className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
                                      />
                                    </div>
                                  )}
                                  
                                  <div
                                    className={`max-w-sm px-4 py-2 rounded-lg text-sm ${
                                      deleteMode && selectedMessages.has(message._id)
                                        ? 'ring-2 ring-red-500'
                                        : ''
                                    } ${
                                      isAIMessage
                                        ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-100 border border-purple-200 dark:border-purple-700 mr-16'
                                        : isMyMessage
                                        ? 'bg-blue-500 text-white ml-16'
                                        : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-600 mr-16'
                                    }`}
                                  >
                                    {(isGroupChat || !isMyMessage || isAIMessage) && (
                                      <div className="text-xs font-medium mb-1" style={{
                                        color: isAIMessage ? '#9333EA' : isMyMessage ? '#BFDBFE' : '#6B7280'
                                      }}>
                                        {isAIMessage ? '🤖 AI Asistentka Vyjednáme' : sender?.name || 'Neznámý uživatel'}
                                      </div>
                                    )}
                                    
                                    <div className="break-words">
                                      {message.isTyping ? (
                                        <div className="flex items-center gap-1">
                                          <div className="flex space-x-1">
                                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                          </div>
                                          <span className="text-purple-600 dark:text-purple-300">Generování odpovědi...</span>
                                        </div>
                                      ) : (
                                        <div 
                                          className="formatted-text"
                                          dangerouslySetInnerHTML={{
                                            __html: message.text
                                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                              .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                              .replace(/🔹 (.*?)(?=\n|$)/g, '<div class="bullet-point">🔹 $1</div>')
                                              .replace(/- (.*?)(?=\n|$)/g, '<div class="bullet-point">• $1</div>')
                                              .replace(/\n/g, '<br>')
                                          }}
                                        />
                                      )}
                                    </div>
                                    
                                    <div className={`text-xs mt-1 flex items-center justify-between ${
                                      isAIMessage 
                                        ? 'text-purple-600 dark:text-purple-300'
                                        : isMyMessage 
                                        ? 'text-blue-100' 
                                        : 'text-gray-500 dark:text-gray-400'
                                    }`}>
                                      <span>
                                        {new Date(message.timestamp || message.createdAt).toLocaleTimeString('cs-CZ', { 
                                          hour: '2-digit', 
                                          minute: '2-digit' 
                                        })}
                                      </span>
                                      
                                      {/* Tlačítko pro opravu AI odpovědi */}
                                      {isAIMessage && !message.isTyping && (
                                        <button
                                          onClick={() => setCorrectingAI(message._id)}
                                          className="ml-2 text-xs bg-purple-500 hover:bg-purple-600 text-white px-1 py-1 rounded transition-colors"
                                          title="Opravit AI odpověď"
                                        >
                                          🔧
                                        </button>
                                      )}
                                      
                                      {/* Status čtení pouze pro zprávy od aktuálního uživatele, ne pro AI */}
                                      {isMyMessage && !isAIMessage && (
                                        <span className="ml-2 flex items-center gap-1">
                                          {message.isGroupMessage ? (
                                            // Skupinové zprávy - ukáže počet lidí, kteří si přečetli
                                            message.readBy && message.readBy.length > 0 ? (
                                              <span 
                                                className="text-xs cursor-help" 
                                                title={`Přečetlo ${message.readBy.length} lidí`}
                                              >
                                                ✓✓ {message.readBy.length}
                                              </span>
                                            ) : (
                                              <span 
                                                className="text-xs opacity-60 cursor-help" 
                                                title="Odesláno"
                                              >
                                                ✓
                                              </span>
                                            )
                                          ) : (
                                            // Soukromé zprávy - jednoduchý status
                                            message.read ? (
                                              <span 
                                                className="text-xs cursor-help" 
                                                title="Zobrazeno"
                                              >
                                                ✓✓
                                              </span>
                                            ) : (
                                              <span 
                                                className="text-xs opacity-60 cursor-help" 
                                                title="Odesláno"
                                              >
                                                ✓
                                              </span>
                                            )
                                          )}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Message input pro mobil - s fixem pro iPhone Safari */}
                    <div className="border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 relative ios-chat-fix" style={{
                      paddingTop: '16px',
                      paddingLeft: '16px', 
                      paddingRight: '16px',
                      paddingBottom: 'calc(60px + env(safe-area-inset-bottom))', // Zvětšený padding pro URL panel
                      minHeight: 'calc(120px + env(safe-area-inset-bottom))', // Větší minimální výška
                      position: 'sticky',
                      bottom: '0',
                      zIndex: '1000',
                      // iOS Safari specifické opravy
                      WebkitTransform: 'translateZ(0)', // Hardware acceleration
                      transform: 'translateZ(0)',
                      WebkitBackfaceVisibility: 'hidden',
                      backfaceVisibility: 'hidden'
                    }}>
                      {showEmojis && (
                        <div className="absolute left-4 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-3 grid grid-cols-8 gap-2 z-10" style={{
                          bottom: 'calc(120px + env(safe-area-inset-bottom))' // Posun emoji palette nad větší safe area
                        }}>
                          {emojis.map((emoji, index) => (
                            <button
                              key={index}
                              onClick={() => addEmoji(emoji)}
                              className="text-xl hover:bg-gray-100 dark:hover:bg-gray-600 p-1 rounded transition-colors"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Media preview */}
                      {mediaPreview && isDiscordChat && (
                        <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                          <div className="flex items-center gap-2">
                            {mediaPreview.type.startsWith('image/') ? (
                              <img src={mediaPreview.url} alt="Preview" className="w-16 h-16 object-cover rounded" />
                            ) : (
                              <video src={mediaPreview.url} className="w-16 h-16 object-cover rounded" />
                            )}
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{mediaPreview.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {mediaPreview.type.startsWith('image/') ? 'Obrázek' : 'Video'}
                              </div>
                            </div>
                            <button
                              onClick={cancelMediaSelection}
                              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-2"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3 items-center">
                        <button
                          onClick={() => setShowEmojis(!showEmojis)}
                          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                        >
                          😀
                        </button>
                        
                        {/* Tlačítko pro nahrání média (pouze pro Discord) */}
                        {isDiscordChat && (
                          <>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*,video/*"
                              onChange={handleFileSelect}
                              className="hidden"
                            />
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                            >
                              📎
                            </button>
                          </>
                        )}
                        
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={handleKeyPress}
                          onFocus={(e) => {
                            // iOS Safari hack - scroll input do view při focus
                            setTimeout(() => {
                              e.target.scrollIntoView({ 
                                behavior: 'smooth', 
                                block: 'center',
                                inline: 'nearest'
                              });
                            }, 300);
                          }}
                          placeholder="Napište zprávu..."
                          className="flex-1 px-4 py-4 border border-gray-300 dark:border-gray-500 rounded-full text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          style={{
                            fontSize: '16px', // Fix pro iOS zoom
                            minHeight: '60px', // Větší minimální výška pro lepší dostupnost
                            lineHeight: '1.5',
                            // iOS Safari specifické opravy
                            WebkitAppearance: 'none',
                            appearance: 'none',
                            WebkitBorderRadius: '25px',
                            borderRadius: '25px',
                            // Prevence iOS Safari UI překrytí
                            marginBottom: '20px' // Větší margin pro URL panel
                          }}
                        />
                        <button
                          onClick={sendMessage}
                          disabled={(!newMessage.trim() && !selectedMediaFile) || (!isGroupChat && !selectedUser && !isAIChat && !isDiscordChat)}
                          className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white px-4 py-4 rounded-full transition-colors flex-shrink-0"
                          style={{
                            minHeight: '60px',
                            minWidth: '60px'
                          }}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              // Desktop verze
              <>
                {/* Users list pro desktop */}
                <div className="w-40 sm:w-60 bg-gray-50 dark:bg-gray-700 border-r border-gray-200 dark:border-gray-600 overflow-y-auto custom-scrollbar flex-shrink-0">
                  <div className="p-2">
                    {/* Komunikační služby */}
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">KOMUNIKACE</div>
                    
                    {/* AI Chat */}
                    <button
                      type="button"
                      onClick={(e) => handleAIChatSelect(e)}
                      className={`w-full text-left p-2 rounded text-sm transition-colors mb-2 ${
                        isAIChat 
                          ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-500' 
                          : 'hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-500 dark:bg-purple-600 rounded-full flex items-center justify-center text-xs font-semibold text-white">
                          🤖
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-sm font-medium">AI Asistentka</div>
                        </div>
                      </div>
                    </button>

                    {/* Discord - přihlášení nebo výběr serveru */}
                    {!discordAuth ? (
                      <button
                        type="button"
                        onClick={(e) => handleDiscordSelect(e)}
                        className="w-full text-left p-2 rounded text-sm transition-colors mb-3 bg-indigo-50 dark:bg-indigo-900 hover:bg-indigo-100 dark:hover:bg-indigo-800 text-gray-900 dark:text-gray-100 border-2 border-indigo-300 dark:border-indigo-500"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-indigo-600 dark:bg-indigo-700 rounded-full flex items-center justify-center text-xs font-semibold text-white">
                            💬
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="truncate text-sm font-medium">Připojit Discord</div>
                          </div>
                        </div>
                      </button>
                    ) : (
                      <>
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 mt-3">
                          {discordChannels[0]?.type === 'guild' ? 'DISCORD SERVERY' : 'DISCORD KANÁLY'}
                        </div>
                        {isLoadingDiscord ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                          </div>
                        ) : discordChannels.length > 0 ? (
                          <>
                            {/* Pokud zobrazujeme guildy (servery) */}
                            {discordChannels[0]?.type === 'guild' ? (
                              discordChannels.map(channel => (
                                <button
                                  key={channel.id}
                                  type="button"
                                  onClick={(e) => handleDiscordChannelSelect(e, channel)}
                                  className="w-full text-left p-2 rounded text-sm transition-colors mb-1 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100"
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-indigo-500 dark:bg-gray-500 rounded flex items-center justify-center text-xs font-semibold text-white">
                                      🖥️
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="truncate text-sm font-medium">{channel.name}</div>
                                    </div>
                                  </div>
                                </button>
                              ))
                            ) : (
                              <>
                                {/* Nekategorizované kanály */}
                                {discordUncategorized.map(channel => (
                                  <button
                                    key={channel.id}
                                    type="button"
                                    onClick={(e) => handleDiscordChannelSelect(e, channel)}
                                    className={`w-full text-left p-2 rounded text-sm transition-colors mb-1 ${
                                      selectedDiscordChannel?.id === channel.id 
                                        ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-500' 
                                        : 'hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100'
                                    }`}
                                  >
                                    <div className="flex items-center gap-1">
                                      <span className="text-gray-500 dark:text-gray-400 text-xs">#</span>
                                      <span className="text-xs">{channel.name}</span>
                                    </div>
                                  </button>
                                ))}

                                {/* Kategorie s kanály */}
                                {discordCategories.map(category => (
                                  <div key={category.id} className="mb-1">
                                    <button
                                      type="button"
                                      onClick={() => toggleCategory(category.id)}
                                      className="w-full text-left p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                                    >
                                      <div className="flex items-center gap-1">
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {expandedCategories.has(category.id) ? '▼' : '▶'}
                                        </span>
                                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">
                                          {category.name}
                                        </span>
                                      </div>
                                    </button>
                                    
                                    {expandedCategories.has(category.id) && discordChannelsByCategory[category.id] && (
                                      <div className="ml-3 mt-1 space-y-1">
                                        {discordChannelsByCategory[category.id].map(channel => (
                                          <button
                                            key={channel.id}
                                            type="button"
                                            onClick={(e) => handleDiscordChannelSelect(e, channel)}
                                            className={`w-full text-left p-2 rounded text-sm transition-colors ${
                                              selectedDiscordChannel?.id === channel.id 
                                                ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-500' 
                                                : 'hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100'
                                            }`}
                                          >
                                            <div className="flex items-center gap-1">
                                              <span className="text-gray-500 dark:text-gray-400 text-xs">#</span>
                                              <span className="text-xs">{channel.name}</span>
                                            </div>
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}

                                {/* Tlačítko zpět na servery */}
                                <button
                                  type="button"
                                  onClick={() => loadDiscordGuilds()}
                                  className="w-full text-left p-2 rounded text-sm transition-colors mt-2 text-indigo-600 dark:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-600"
                                >
                                  ← Zpět na servery
                                </button>
                              </>
                            )}
                          </>
                        ) : (
                          <div className="text-xs text-gray-500 dark:text-gray-400 p-2">Načítání...</div>
                        )}
                      </>
                    )}

                    {/* Seznam uživatelů - SKRYTÝ */}
                    {false && hasPermission('viewAllUsers') && (
                      <>
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 mt-4">UŽIVATELÉ (DEAKTIVOVÁNO)</div>
                        {visibleUsers.map(user => (
                      <button
                        key={user._id}
                        type="button"
                        onClick={(e) => handleUserSelect(e, user)}
                        className={`w-full text-left p-2 rounded text-sm transition-colors mb-1 ${
                          selectedUser?._id === user._id 
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-500' 
                            : 'hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <div className="w-8 h-8 bg-gray-300 dark:bg-gray-500 rounded-full flex items-center justify-center text-xs font-semibold text-gray-700 dark:text-gray-200">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            {onlineUsers.has(user._id) && (
                              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 dark:bg-green-400 rounded-full border-2 border-white dark:border-gray-800"></div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="truncate text-sm font-medium">
                              <span className="sm:hidden">{user.name.split(' ')[0]}</span>
                              <span className="hidden sm:inline">{user.name}</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                              {onlineUsers.has(user._id) ? 'Online' : 'Offline'}
                            </div>
                          </div>
                          
                          {/* Badge pro nepřečtené zprávy od tohoto uživatele */}
                          {unreadByUser[user._id] && unreadByUser[user._id].length > 0 && (
                            <div className="bg-red-500 dark:bg-red-400 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                              {unreadByUser[user._id].length > 9 ? '9+' : unreadByUser[user._id].length}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                      </>
                    )}
                  </div>
                </div>

                {/* Chat area pro desktop */}
                <div className="flex-1 flex flex-col">
                  {selectedUser || isGroupChat || isAIChat || isDiscordChat ? (
                    <>
                      {/* AI Chat má nejvyšší prioritu */}
                      {isAIChat ? (
                        <>
                          {/* AI Chat Messages */}
                          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 bg-gray-50 dark:bg-gray-900">
                            {isLoadingMessages ? (
                              <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                              </div>
                            ) : messages.length === 0 ? (
                              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400 text-sm">
                                👋 Začněte konverzaci s AI asistentkou
                              </div>
                            ) : (
                              <div className="space-y-4">
                                {messages.map(message => {
                                  const isAIMessage = message.isAI || message.senderId === 'ai';
                                  const isMyMessage = !isAIMessage;
                                  
                                  return (
                                    <div
                                      key={message._id}
                                      className={`flex w-full ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                                    >
                                      <div className={`max-w-sm px-4 py-2 rounded-lg text-sm ${
                                        isAIMessage
                                          ? 'bg-purple-100 dark:bg-purple-900 text-gray-800 dark:text-gray-100 border border-purple-200 dark:border-purple-700 mr-16'
                                          : 'bg-blue-600 text-white ml-16'
                                      }`}>
                                        {isAIMessage && (
                                          <div className="text-xs font-medium mb-1 text-purple-600 dark:text-purple-400">
                                            🤖 AI Asistentka
                                          </div>
                                        )}
                                        <div className="break-words whitespace-pre-wrap">{message.text}</div>
                                        <div className={`text-xs mt-1 ${isAIMessage ? 'text-purple-600 dark:text-purple-400' : 'text-blue-100'}`}>
                                          {new Date(message.timestamp || message.createdAt).toLocaleTimeString('cs-CZ', { 
                                            hour: '2-digit', 
                                            minute: '2-digit' 
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div ref={messagesEndRef} />
                          </div>

                          {/* AI Chat Input */}
                          <div className="border-t border-gray-200 dark:border-gray-600 p-4 bg-white dark:bg-gray-800 relative">
                            {showEmojis && (
                              <div className="absolute bottom-20 left-4 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-3 grid grid-cols-8 gap-2 z-10">
                                {emojis.map((emoji, index) => (
                                  <button
                                    key={index}
                                    onClick={() => addEmoji(emoji)}
                                    className="text-xl hover:bg-gray-100 dark:hover:bg-gray-600 p-1 rounded transition-colors"
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </div>
                            )}
                            
                            <div className="flex gap-3">
                              <button
                                onClick={() => setShowEmojis(!showEmojis)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                              >
                                😀
                              </button>
                              
                              <input
                                type="text"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Zeptejte se AI asistentky..."
                                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-500 rounded-full text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                              <button
                                onClick={sendMessage}
                                disabled={!newMessage.trim()}
                                className="bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white px-4 py-3 rounded-full transition-colors flex-shrink-0"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </>
                      ) : isDiscordChat && !discordAuth ? (
                        <div className="flex-1 flex items-center justify-center p-8 bg-gray-50 dark:bg-gray-900">
                          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                            <div className="text-center mb-6">
                              <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-3xl">💬</span>
                              </div>
                              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                Připojení k Discord
                              </h2>
                              <p className="text-gray-600 dark:text-gray-400">
                                Přihlaste se k Discord účtu pro zobrazení vašich serverů a kanálů
                              </p>
                            </div>
                            
                            <div className="space-y-4">
                              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                <p className="text-sm text-blue-800 dark:text-blue-300">
                                  <strong>ℹ️ Co získáte:</strong>
                                </p>
                                <ul className="mt-2 text-sm text-blue-700 dark:text-blue-400 space-y-1">
                                  <li>✅ Přístup ke všem vašim Discord serverům</li>
                                  <li>✅ Zobrazení textových kanálů</li>
                                  <li>✅ Odesílání a čtení zpráv</li>
                                  <li>✅ Automatická synchronizace</li>
                                </ul>
                              </div>
                              
                              <button
                                onClick={handleDiscordLogin}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                              >
                                <span className="text-xl">💬</span>
                                Přihlásit se k Discord
                              </button>
                              
                              <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                                Bezpečné připojení přes Discord OAuth2. Vaše přihlašovací údaje nejsou ukládány v CRM.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : isDiscordChat && discordAuth ? (
                        <>
                          {/* Discord Channel Selector and Messages */}
                          <div className="border-b border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white">
                                  💬
                                </div>
                                <div>
                                  <h3 className="font-semibold text-gray-900 dark:text-white">
                                    {selectedDiscordChannel ? `#${selectedDiscordChannel.name}` : 'Discord'}
                                  </h3>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {selectedDiscordChannel ? 'Text kanál' : 'Vyberte kanál'}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={handleDiscordLogout}
                                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Odpojit
                              </button>
                            </div>
                            
                            {/* Discord Channels List */}
                            {discordChannels.length > 0 && !selectedDiscordChannel && (
                              <div className="mt-4 space-y-2">
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Dostupné kanály:</p>
                                {discordChannels.map(channel => (
                                  <button
                                    key={channel.id}
                                    onClick={() => handleDiscordChannelSelect(channel)}
                                    className="w-full text-left px-3 py-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                                  >
                                    # {channel.name}
                                  </button>
                                ))}
                              </div>
                            )}
                            
                            {selectedDiscordChannel && (
                              <button
                                onClick={() => setSelectedDiscordChannel(null)}
                                className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
                              >
                                ← Zpět na seznam kanálů
                              </button>
                            )}
                          </div>
                          
                          {/* Discord Messages */}
                          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 bg-gray-50 dark:bg-gray-900">
                            {isLoadingMessages ? (
                              <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                              </div>
                            ) : !selectedDiscordChannel ? (
                              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400 text-sm">
                                Vyberte kanál pro zobrazení zpráv
                              </div>
                            ) : messages.length === 0 ? (
                              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400 text-sm">
                                Žádné zprávy v tomto kanálu
                              </div>
                            ) : (
                              <div className="space-y-4">
                                {messages.map(message => {
                                  const isMyMessage = message.sender?._id === currentUserId;
                                  
                                  return (
                                    <div
                                      key={message._id}
                                      className={`flex w-full ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                                    >
                                      <div className={`max-w-sm px-4 py-2 rounded-lg text-sm ${
                                        isMyMessage
                                          ? 'bg-indigo-600 text-white ml-16'
                                          : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-600 mr-16'
                                      }`}>
                                        {!isMyMessage && (
                                          <div className="text-xs font-medium mb-1 text-indigo-600 dark:text-indigo-400">
                                            {message.sender?.name || 'Discord User'}
                                          </div>
                                        )}
                                        
                                        {/* Textový obsah */}
                                        {message.text && (
                                          <div className="break-words whitespace-pre-wrap">{message.text}</div>
                                        )}
                                        
                                        {/* Přílohy - obrázky */}
                                        {message.attachments && message.attachments.length > 0 && (
                                          <div className="mt-2 space-y-2">
                                            {message.attachments.map((att, idx) => (
                                              <div key={idx}>
                                                {att.content_type?.startsWith('image/') ? (
                                                  <a href={att.url} target="_blank" rel="noopener noreferrer">
                                                    <img 
                                                      src={att.url} 
                                                      alt={att.filename}
                                                      className="max-w-full rounded cursor-pointer hover:opacity-90"
                                                      style={{ maxHeight: '300px' }}
                                                    />
                                                  </a>
                                                ) : (
                                                  <a 
                                                    href={att.url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className={`inline-flex items-center gap-2 px-3 py-1 rounded ${
                                                      isMyMessage 
                                                        ? 'bg-indigo-500 hover:bg-indigo-400' 
                                                        : 'bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500'
                                                    }`}
                                                  >
                                                    📎 {att.filename}
                                                  </a>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        
                                        {/* Embeds */}
                                        {message.embeds && message.embeds.length > 0 && (
                                          <div className="mt-2 space-y-2">
                                            {message.embeds.map((embed, idx) => (
                                              <div 
                                                key={idx}
                                                className={`p-2 rounded border-l-4 ${
                                                  isMyMessage 
                                                    ? 'bg-indigo-500 border-indigo-300' 
                                                    : 'bg-gray-100 dark:bg-gray-600 border-gray-400 dark:border-gray-500'
                                                }`}
                                              >
                                                {embed.title && (
                                                  <div className="font-semibold text-sm">{embed.title}</div>
                                                )}
                                                {embed.description && (
                                                  <div className="text-xs mt-1">{embed.description}</div>
                                                )}
                                                {embed.image?.url && (
                                                  <img 
                                                    src={embed.image.url} 
                                                    alt="Embed"
                                                    className="mt-2 max-w-full rounded"
                                                    style={{ maxHeight: '200px' }}
                                                  />
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        
                                        <div className={`text-xs mt-1 ${isMyMessage ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400'}`}>
                                          {new Date(message.createdAt).toLocaleTimeString('cs-CZ', { 
                                            hour: '2-digit', 
                                            minute: '2-digit' 
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div ref={messagesEndRef} />
                          </div>
                          
                          {/* Discord Input */}
                          {selectedDiscordChannel && (
                            <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={newMessage}
                                  onChange={(e) => setNewMessage(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter' && newMessage.trim()) {
                                      sendDiscordMessage(newMessage);
                                      setNewMessage('');
                                    }
                                  }}
                                  placeholder="Napište zprávu do Discord..."
                                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                                />
                                <button
                                  onClick={() => {
                                    if (newMessage.trim()) {
                                      sendDiscordMessage(newMessage);
                                      setNewMessage('');
                                    }
                                  }}
                                  disabled={!newMessage.trim()}
                                  className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white px-6 py-2 rounded-full transition-colors"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      ) : !isDiscordChat ? (
                        <>
                          {/* Messages */}
                          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 bg-gray-50 dark:bg-gray-900">
                        {messages.length === 0 ? (
                          <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400 text-sm">
                            Zatím žádné zprávy
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {messages.map(message => {
                              const senderId = typeof message.senderId === 'object' ? message.senderId._id : message.senderId;
                              const isMyMessage = String(senderId) === String(currentUserId);
                              const isAIMessage = message.isAI || senderId === 'ai';
                              const sender = users.find(u => String(u._id) === String(senderId));
                              
                              return (
                                <div
                                  key={message._id || `msg_${Date.now()}_${Math.random()}`}
                                  className={`flex w-full ${isMyMessage ? 'justify-end' : 'justify-start'} ${
                                    deleteMode ? 'group cursor-pointer' : ''
                                  }`}
                                  onClick={() => deleteMode && toggleMessageSelection(message._id)}
                                >
                                  <div className="relative">
                                    {deleteMode && isAdmin && !isAIMessage && (
                                      <div className="absolute -left-8 top-1/2 transform -translate-y-1/2">
                                        <input
                                          type="checkbox"
                                          checked={selectedMessages.has(message._id)}
                                          onChange={() => toggleMessageSelection(message._id)}
                                          className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
                                        />
                                      </div>
                                    )}
                                    
                                    <div
                                      className={`max-w-sm px-4 py-2 rounded-lg text-sm ${
                                        deleteMode && selectedMessages.has(message._id)
                                          ? 'ring-2 ring-red-500'
                                          : ''
                                      } ${
                                        isAIMessage
                                          ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-100 border border-purple-200 dark:border-purple-700 mr-16'
                                          : isMyMessage
                                          ? 'bg-blue-500 text-white ml-16'
                                          : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-600 mr-16'
                                      }`}
                                    >
                                      {(isGroupChat || !isMyMessage || isAIMessage) && (
                                        <div className="text-xs font-medium mb-1" style={{
                                          color: isAIMessage ? '#9333EA' : isMyMessage ? '#BFDBFE' : '#6B7280'
                                        }}>
                                          {isAIMessage ? '🤖 AI Asistentka Vyjednáme' : sender?.name || 'Neznámý uživatel'}
                                        </div>
                                      )}
                                      
                                      <div className="break-words">
                                        {message.isTyping ? (
                                          <div className="flex items-center gap-1">
                                            <div className="flex space-x-1">
                                              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                                              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                            </div>
                                            <span className="text-purple-600 dark:text-purple-300">Generování odpovědi...</span>
                                          </div>
                                        ) : (
                                          <div 
                                            className="formatted-text"
                                            dangerouslySetInnerHTML={{
                                              __html: message.text
                                                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                                .replace(/🔹 (.*?)(?=\n|$)/g, '<div class="bullet-point">🔹 $1</div>')
                                                .replace(/- (.*?)(?=\n|$)/g, '<div class="bullet-point">• $1</div>')
                                                .replace(/\n/g, '<br>')
                                            }}
                                          />
                                        )}
                                      </div>
                                      
                                      <div className={`text-xs mt-1 flex items-center justify-between ${
                                        isAIMessage 
                                          ? 'text-purple-600 dark:text-purple-300'
                                          : isMyMessage 
                                          ? 'text-blue-100' 
                                          : 'text-gray-500 dark:text-gray-400'
                                      }`}>
                                        <span>
                                          {new Date(message.timestamp || message.createdAt).toLocaleTimeString('cs-CZ', { 
                                            hour: '2-digit', 
                                            minute: '2-digit' 
                                          })}
                                        </span>
                                        
                                        {/* Tlačítko pro opravu AI odpovědi */}
                                        {isAIMessage && !message.isTyping && (
                                          <button
                                            onClick={() => setCorrectingAI(message._id)}
                                            className="ml-2 text-xs bg-purple-500 hover:bg-purple-600 text-white px-1 py-1 rounded transition-colors"
                                            title="Opravit AI odpověď"
                                          >
                                            🔧
                                          </button>
                                        )}
                                        
                                        {/* Status čtení pouze pro zprávy od aktuálního uživatele, ne pro AI */}
                                        {isMyMessage && !isAIMessage && (
                                          <span className="ml-2 flex items-center gap-1">
                                            {message.isGroupMessage ? (
                                              // Skupinové zprávy - ukáže počet lidí, kteří si přečetli
                                              message.readBy && message.readBy.length > 0 ? (
                                                <span 
                                                  className="text-xs cursor-help" 
                                                  title={`Přečetlo ${message.readBy.length} lidí`}
                                                >
                                                  ✓✓ {message.readBy.length}
                                                </span>
                                              ) : (
                                                <span 
                                                  className="text-xs opacity-60 cursor-help" 
                                                  title="Odesláno"
                                                >
                                                  ✓
                                                </span>
                                              )
                                            ) : (
                                              // Soukromé zprávy - jednoduchý status
                                              message.read ? (
                                                <span 
                                                  className="text-xs cursor-help" 
                                                  title="Zobrazeno"
                                                >
                                                  ✓✓
                                                </span>
                                              ) : (
                                                <span 
                                                  className="text-xs opacity-60 cursor-help" 
                                                  title="Odesláno"
                                                >
                                                  ✓
                                                </span>
                                              )
                                            )}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>

                      {/* Message input pro desktop */}
                      <div className="border-t border-gray-200 dark:border-gray-600 p-4 bg-white dark:bg-gray-800 relative">
                        {showEmojis && (
                          <div className="absolute bottom-20 left-4 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-3 grid grid-cols-8 gap-2 z-10">
                            {emojis.map((emoji, index) => (
                              <button
                                key={index}
                                onClick={() => addEmoji(emoji)}
                                className="text-xl hover:bg-gray-100 dark:hover:bg-gray-600 p-1 rounded transition-colors"
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {/* Media preview pro desktop */}
                        {mediaPreview && isDiscordChat && (
                          <div className="mb-3 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                            <div className="flex items-center gap-2">
                              {mediaPreview.type.startsWith('image/') ? (
                                <img src={mediaPreview.url} alt="Preview" className="w-12 h-12 object-cover rounded" />
                              ) : (
                                <video src={mediaPreview.url} className="w-12 h-12 object-cover rounded" />
                              )}
                              <div className="flex-1">
                                <div className="text-xs font-medium text-gray-900 dark:text-gray-100">{mediaPreview.name}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {mediaPreview.type.startsWith('image/') ? 'Obrázek' : 'Video'}
                                </div>
                              </div>
                              <button
                                onClick={cancelMediaSelection}
                                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-xs"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="flex gap-3">
                          <button
                            onClick={() => setShowEmojis(!showEmojis)}
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                          >
                            😀
                          </button>
                          
                          {/* Tlačítko pro nahrání média (pouze pro Discord) - desktop */}
                          {isDiscordChat && (
                            <>
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,video/*"
                                onChange={handleFileSelect}
                                className="hidden"
                              />
                              <button
                                onClick={() => fileInputRef.current?.click()}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                              >
                                📎
                              </button>
                            </>
                          )}
                          
                          <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Napište zprávu..."
                            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-500 rounded-full text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <button
                            onClick={sendMessage}
                            disabled={(!newMessage.trim() && !selectedMediaFile) || (!isGroupChat && !selectedUser && !isAIChat && !isDiscordChat)}
                            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white px-4 py-3 rounded-full transition-colors flex-shrink-0"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </>
                      ) : null}
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm">
                      Vyberte uživatele, skupinový chat, AI nebo Discord pro zahájení konverzace
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Click outside to close emoji palette */}
      {showEmojis && (
        <div 
          className="fixed inset-0 z-5"
          onClick={() => setShowEmojis(false)}
        />
      )}

      {/* Modal pro opravu AI odpovědi */}
      {correctingAI && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 15000 }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto custom-scrollbar">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                🔧 Opravit AI odpověď
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Napište správnou odpověď:
                </label>
                <textarea
                  value={correctionText}
                  onChange={(e) => setCorrectionText(e.target.value)}
                  placeholder="Zde napište správnou odpověď, kterou měla AI poskytnout..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="6"
                />
              </div>
              
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                💡 Tato oprava bude automaticky uložena do AI Data Manageru, aby se AI učila z chyb a příště odpověděla správně.
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setCorrectingAI(null);
                    setCorrectionText('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition-colors"
                >
                  Zrušit
                </button>
                <button
                  onClick={() => {
                    const messageToCorrect = aiMessages.find(msg => msg._id === correctingAI);
                    const userMessage = aiMessages[aiMessages.findIndex(msg => msg._id === correctingAI) - 1];
                    
                    if (messageToCorrect && correctionText.trim()) {
                      handleCorrectAI(
                        correctingAI,
                        userMessage?.text || 'Neznámý dotaz',
                        messageToCorrect.text,
                        correctionText.trim()
                      );
                    }
                  }}
                  disabled={!correctionText.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed rounded-md transition-colors"
                >
                  💾 Uložit opravu
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
